import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, FORM_CONTROL_CLASS, FORM_LABEL_CLASS, FORM_TEXTAREA_CLASS, Modal } from '@safetyhub/ui';
import { formatDateTimeISO, type AdminLegalObligationRow } from '@safetyhub/shared';
import { extractApiError } from '../../lib/api';
import {
  OBLIGATION_TEMPLATE_LINK_QUERY_KEYS,
  OBLIGATION_TEMPLATE_LINK_ROLE_LABEL,
  obligationTemplateLinksApi,
  type ObligationTemplateLink,
  type ObligationTemplateLinkRole,
} from '../../lib/legalObligationTemplateLinks';
import { useToastStore } from '../../stores/toast';

const ROLES = Object.keys(OBLIGATION_TEMPLATE_LINK_ROLE_LABEL) as ObligationTemplateLinkRole[];

/**
 * 의무 하나에 쓸 양식을 걸고 해제하는 모달 — L24(US-0723) 「링크 저작 입구」.
 *
 * 이 입구가 생기기 전에는 의무 ↔ 양식 링크를 만드는 코드가 없어 양식 삭제 경고가 **영영 발동하지 않았다**
 * (계획서 §C-2). 판정(양식 실재 · 한 양식 한 역할 · 정본은 의무당 하나)은 **서버가 한다** — 이 화면은 서버의
 * 거부 문구를 그대로 토스트로 보여 준다(같은 판정을 화면에 한 벌 더 두면 둘이 어긋난다).
 *
 * 해제는 행을 지우지 않는다 — 「해제된 연결」에 이력으로 남고, 같은 양식·역할로 다시 걸면 그 행이 되살아난다.
 */
export default function ObligationTemplateLinksModal({
  obligation,
  onClose,
}: {
  obligation: AdminLegalObligationRow;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { showToast } = useToastStore();
  const [templateKey, setTemplateKey] = useState('');
  const [role, setRole] = useState<ObligationTemplateLinkRole>('PRIMARY');
  const [note, setNote] = useState('');

  const linksKey = OBLIGATION_TEMPLATE_LINK_QUERY_KEYS.links(obligation.id);
  const links = useQuery({
    queryKey: linksKey,
    queryFn: () => obligationTemplateLinksApi.list(obligation.id),
  });
  const options = useQuery({
    queryKey: OBLIGATION_TEMPLATE_LINK_QUERY_KEYS.options,
    queryFn: () => obligationTemplateLinksApi.options(),
  });

  const rows = links.data ?? [];
  const current = rows.filter((l) => l.isCurrent);
  const released = rows.filter((l) => !l.isCurrent);

  const link = useMutation({
    mutationFn: () =>
      obligationTemplateLinksApi.link(obligation.id, {
        templateKey,
        role,
        note: note.trim() === '' ? null : note.trim(),
      }),
    onSuccess: () => {
      showToast('양식을 연결했습니다.', 'success');
      setTemplateKey('');
      setNote('');
      void queryClient.invalidateQueries({ queryKey: linksKey });
    },
    onError: (error) => showToast(extractApiError(error).message, 'error'),
  });

  const unlink = useMutation({
    mutationFn: (linkId: string) => obligationTemplateLinksApi.unlink(obligation.id, linkId),
    onSuccess: () => {
      showToast('연결을 해제했습니다. 해제된 연결에 이력으로 남습니다.', 'success');
      void queryClient.invalidateQueries({ queryKey: linksKey });
    },
    onError: (error) => showToast(extractApiError(error).message, 'error'),
  });

  const busy = link.isPending || unlink.isPending;

  return (
    <Modal open onClose={onClose} title={`쓸 양식 — ${obligation.title}`} size="xl">
      <p className="mb-3 text-sm text-ink-600">
        이 의무를 이행할 때 쓰는 양식을 연결합니다. <strong>정본</strong>은 의무당 하나이고 「작성 시작」이 그
        양식을 엽니다. 비공개 양식에 연결하면 공개할 때까지 「양식 준비 중」으로 보입니다.
      </p>

      {links.isLoading ? (
        <div className="py-6 text-center text-gray-500">불러오는 중...</div>
      ) : links.error ? (
        <div className="p-3 bg-red-50 border border-red-200 rounded-card text-sm text-red-700">
          연결을 불러오지 못했습니다. {extractApiError(links.error).message}
        </div>
      ) : (
        <>
          <h3 className="mb-2 text-sm font-semibold text-ink-900">연결된 양식</h3>
          {current.length === 0 ? (
            <div data-testid="template-links-empty" className="mb-4 py-4 text-center text-sm text-gray-500">
              연결된 양식이 없습니다.
            </div>
          ) : (
            <ul className="mb-4 space-y-2">
              {current.map((l) => (
                <LinkItem
                  key={l.id}
                  link={l}
                  action={
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={busy}
                      onClick={() => unlink.mutate(l.id)}
                    >
                      해제
                    </Button>
                  }
                />
              ))}
            </ul>
          )}

          {released.length > 0 && (
            <details className="mb-4">
              <summary className="cursor-pointer text-sm text-ink-600">해제된 연결 {released.length}</summary>
              <ul className="mt-2 space-y-2">
                {released.map((l) => (
                  <LinkItem key={l.id} link={l} />
                ))}
              </ul>
            </details>
          )}
        </>
      )}

      <div className="rounded-card border border-line p-3">
        <h3 className="mb-2 text-sm font-semibold text-ink-900">양식 연결</h3>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className={FORM_LABEL_CLASS}>양식</span>
            <select
              aria-label="연결할 양식"
              className={FORM_CONTROL_CLASS}
              value={templateKey}
              onChange={(e) => setTemplateKey(e.target.value)}
              disabled={options.isLoading}
            >
              <option value="">{options.isLoading ? '불러오는 중...' : '양식을 고르세요'}</option>
              {(options.data ?? []).map((t) => (
                <option key={t.key} value={t.key}>
                  {t.name}
                  {t.enabled ? '' : ' (비공개)'}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className={FORM_LABEL_CLASS}>역할</span>
            <select
              aria-label="연결 역할"
              className={FORM_CONTROL_CLASS}
              value={role}
              onChange={(e) => setRole(e.target.value as ObligationTemplateLinkRole)}
            >
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {OBLIGATION_TEMPLATE_LINK_ROLE_LABEL[r]}
                </option>
              ))}
            </select>
          </label>
        </div>
        <label className="mt-3 block">
          <span className={FORM_LABEL_CLASS}>메모 (선택)</span>
          <textarea
            aria-label="연결 메모"
            className={FORM_TEXTAREA_CLASS}
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="일부만 충족이면 무엇이 빠지는지 적어 주세요."
          />
        </label>
        {options.error && (
          <div className="mt-2 text-sm text-red-700">
            양식 목록을 불러오지 못했습니다. {extractApiError(options.error).message}
          </div>
        )}
        <div className="mt-3 flex justify-end">
          <Button
            variant="primary"
            size="sm"
            disabled={busy || templateKey === ''}
            title={templateKey === '' ? '양식을 고르세요.' : undefined}
            onClick={() => link.mutate()}
          >
            {link.isPending ? '연결 중...' : '연결'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function LinkItem({ link, action }: { link: ObligationTemplateLink; action?: ReactNode }) {
  return (
    <li
      data-testid={`template-link-${link.id}`}
      className="flex flex-wrap items-start justify-between gap-2 rounded-card border border-line p-3 text-sm"
    >
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold text-ink-900">
            {link.templateName ?? <span className="text-ink-500">삭제된 양식 ({link.templateKey})</span>}
          </span>
          <Badge tone={link.role === 'PRIMARY' ? 'emerald' : 'slate'} size="sm">
            {OBLIGATION_TEMPLATE_LINK_ROLE_LABEL[link.role]}
          </Badge>
          {link.templateName !== null && !link.templateEnabled && (
            <Badge tone="amber" size="sm">
              비공개
            </Badge>
          )}
        </div>
        {link.note && <div className="mt-1 text-ink-600">{link.note}</div>}
        <div className="mt-1 text-xs text-ink-500">
          연결 {formatDateTimeISO(new Date(link.linkedAt)).replace('T', ' ')}
          {link.unlinkedAt && ` · 해제 ${formatDateTimeISO(new Date(link.unlinkedAt)).replace('T', ' ')}`}
        </div>
      </div>
      {action}
    </li>
  );
}
